import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchUserProfile, updateUserProfileApi, updateUserProfileSettingsApi, UserProfiles, Projects, UserProfileSettings } from "@/api";
import { setCurrentProjectId } from "../project/projectSlice";

import type { RootState } from "../store";

interface UserProfileState {
    userProfile: UserProfiles | null;
    status: "idle" | "loading" | "succeeded" | "failed";
    settingsUpdateStatus: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
}

const initialState: UserProfileState = {
    userProfile: null,
    status: "idle",
    settingsUpdateStatus: "idle",
    error: null
};

// Async thunk to fetch user profile
export const fetchUserProfileThunk = createAsyncThunk<UserProfiles, void, { rejectValue: string }>(
    "userProfile/fetchUserProfile",
    async (_, { getState, rejectWithValue }) => {
        const state = getState() as RootState;
        const token = state.clientToken.token;
        const clerk_id = state.clientToken.clerkId;

        if (!token) {
            return rejectWithValue("No token available");
        }

        if (!clerk_id) {
            return rejectWithValue("No clerk ID available");
        }

        try {
            const profile = await fetchUserProfile(clerk_id, token);
            return profile;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to fetch user profile");
        }
    }
);

// Async thunk to update user profile
export const updateUserProfileThunk = createAsyncThunk<UserProfiles, UserProfiles, { rejectValue: string }>(
    "userProfile/updateUserProfile",
    async (newProfile, { getState, rejectWithValue, dispatch }) => {
        const state = getState() as RootState;
        const idToken = state.clientToken.token;

        const clerk_id = state.clientToken.clerkId;

        if (!idToken) {
            return rejectWithValue("No token available");
        }

        if (!clerk_id) {
            return rejectWithValue("No clerk ID available");
        }

        try {
            const updatedProfile = await updateUserProfileApi(newProfile, idToken);
            const updatedFullProfile = await fetchUserProfile(clerk_id, idToken);
            return updatedFullProfile;
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to update user profile");
        }
    }
);

export const updateUserProfileSettingsThunk = createAsyncThunk<{ settings: UserProfileSettings }, UserProfileSettings, { rejectValue: string }>(
    "userProfile/updateUserProfileSettings",
    async (profileSettings, { getState, rejectWithValue, dispatch }) => {
        const state = getState() as RootState;
        const idToken = state.clientToken.token;
        const userProfile = state.userProfiles.userProfile;

        if (!idToken) {
            return rejectWithValue("No token available");
        }

        try {
            if (!userProfile || !userProfile.userid) {
                return rejectWithValue("User profile not found or invalid user ID");
            }

            const updatedProfile = await updateUserProfileSettingsApi(userProfile?.userid, profileSettings, idToken);

            // Update settings in the state if they were passed in
            if (profileSettings.ActiveProjectId) {
                dispatch(setCurrentProjectId(profileSettings.ActiveProjectId));
            }

            return { settings: updatedProfile.settings };
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to update user profile");
        }
    }
);

const userProfileSlice = createSlice({
    name: "userProfile",
    initialState,
    reducers: {
        resetUserProfileState(state) {
            state.status = "idle";
            state.error = null;
        },
        clearUserProfile(state) {
            state.userProfile = null;
            state.status = "idle";
            state.error = null;
        }
    },
    extraReducers: builder => {
        builder
            .addCase(fetchUserProfileThunk.pending, state => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(fetchUserProfileThunk.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.userProfile = action.payload;
            })
            .addCase(fetchUserProfileThunk.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            })
            .addCase(updateUserProfileThunk.pending, state => {
                state.settingsUpdateStatus = "loading";
                state.error = null;
            })
            .addCase(updateUserProfileThunk.fulfilled, (state, action) => {
                state.settingsUpdateStatus = "succeeded";
                state.userProfile = {
                    ...state.userProfile,
                    ...action.payload, // Only overwrite fields present in payload
                    newprofile: false // Set newprofile to false after successful update
                };
            })
            .addCase(updateUserProfileThunk.rejected, (state, action) => {
                state.settingsUpdateStatus = "failed";
                state.error = action.payload as string;
            })
            .addCase(updateUserProfileSettingsThunk.pending, state => {
                state.settingsUpdateStatus = "loading";
                state.error = null;
            })
            .addCase(updateUserProfileSettingsThunk.fulfilled, (state, action) => {
                state.settingsUpdateStatus = "succeeded";
                if (state.userProfile) {
                    state.userProfile.settings = action.payload.settings;
                }
            })
            .addCase(updateUserProfileSettingsThunk.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload as string;
            });
    }
});

export const { resetUserProfileState, clearUserProfile } = userProfileSlice.actions;
export default userProfileSlice.reducer;
